import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import AvatarIcon from 'components/features/utils/AvatarIcon';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useModal } from 'hooks/useModal';
import { getPortfolioAnalysis } from 'config/api';
import {
  ManagementTypeItem,
  ManagementType,
  ManagementItem,
  PortfolioAnalysisItem,
  PortfolioAssetType,
  PortfolioAnalysis,
  TargetId,
} from '../utils/type';
import { useBirthday } from '../utils/birthday';
import { getManagementItemInfo } from '../utils/portfolioInfo';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { PortfolioAnalysisLoading } from '../components/features/portfolio-analysis/share/PortfolioAnalysisLoading';
import { SettingManagementTypeModal } from '../components/features/portfolio-analysis/modal/SettingManagementTypeModal';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';

const InvestmentItem = ({
  managementType,
  initialManagementType,
  goAssetConfirm,
  showModal,
}: {
  managementType: ManagementType;
  initialManagementType: ManagementType;
  goAssetConfirm: () => void;
  showModal: () => void;
}) => {
  const managementItemInfo = getManagementItemInfo(managementType);
  if (managementItemInfo === undefined) return <Loading loading />;

  if (initialManagementType === managementType)
    return (
      <div className="flex flex-col mt-4 relative">
        <div className="flex flex-row items-center bg-teal-500 text-white py-1 px-2 mr-auto rounded-full absolute -top-3 -left-2.5">
          <img
            style={{ width: '15px', height: '15px' }}
            src="/static/images/advisor/asset_icon/icon_check.svg"
            alt="アイコン"
          />
          <div className="text-xs font-bold ml-2">現在の運用タイプ</div>
        </div>
        <div className="border border-teal-500 rounded-lg">
          <div
            className="grid mt-4 mb-2 mr-2 ml-4"
            style={{ gridTemplateColumns: 'auto 1fr' }}>
            <div className="mx-2 flex items-center">
              <img
                style={{ width: '60px', height: '60px' }}
                src={`/static/images/advisor/asset_icon/${managementItemInfo.imgName}.svg`}
                alt="アイコン"
              />
            </div>
            <div className="ml-2">
              <div className="text-base font-bold text-bluegray-700">
                <span className={`${managementItemInfo.titleColor}`}>
                  {managementItemInfo.title}
                </span>
                <span
                  className={`${
                    managementItemInfo.type === ManagementTypeItem.アセプラ8
                      ? ''
                      : 'text-xs'
                  }`}>
                  {managementItemInfo.subTitle}
                </span>
              </div>
              <div className="text-sm">{managementItemInfo.explanation}</div>
            </div>
          </div>
          <div className="border-b border-teal-500 mx-2"></div>
          <div
            className="flex flex-row justify-center text-teal-500 items-center my-1 cursor-pointer"
            onClick={showModal}>
            <div className="text-sm font-bold">詳細</div>
          </div>
        </div>
      </div>
    );

  return (
    <div
      className="border grid rounded-lg mt-2 cursor-pointer"
      style={{ gridTemplateColumns: '1fr auto' }}
      onClick={(): void => goAssetConfirm()}>
      <div
        className="grid my-4 mr-2 ml-4"
        style={{ gridTemplateColumns: 'auto 1fr' }}>
        <div className="flex items-center mr-2">
          <img
            style={{ width: '60px', height: '60px' }}
            src={`/static/images/advisor/asset_icon/${managementItemInfo.imgName}.svg`}
            alt="アイコン"
          />
        </div>
        <div className="ml-2">
          <div className="text-base font-bold text-bluegray-700">
            <span className={`${managementItemInfo.titleColor}`}>
              {managementItemInfo.title}
            </span>
            <span
              className={`${
                managementItemInfo.type === ManagementTypeItem.アセプラ8
                  ? ''
                  : 'text-xs'
              }`}>
              {managementItemInfo.subTitle}
            </span>
          </div>
          <div className="text-sm">{managementItemInfo.explanation}</div>
        </div>
      </div>
      <div className="px-1 justify-center items-center flex flex-row bg-bluegray-50 border-l border-bluegray-100 rounded-r-lg">
        <div className="icon-chevron-right text-bluegray-400 font-bold flex flex-row"></div>
      </div>
    </div>
  );
};

export const PortfolioAnalysisChangeManagementSelectInternal = ({
  targetId,
  portfolioAnalysis,
}: {
  targetId: TargetId;
  portfolioAnalysis: PortfolioAnalysis;
}) => {
  const { goPortfolioAnalysisChangeAssetsConfirm } = useTargetSupportRouting();

  const goAssetConfirm = (type: ManagementType) => {
    goPortfolioAnalysisChangeAssetsConfirm(targetId, type);
  };

  const [isShowModal, { hideModal, showModal }] = useModal();

  const initialManagementType: ManagementType =
    portfolioAnalysis.managementType;
  const managementItems: ManagementItem[] = portfolioAnalysis.managementItems;

  const managementItem = managementItems.find(
    managementItems => initialManagementType === managementItems.managementType
  );
  const initialManagementItemInfo = getManagementItemInfo(
    initialManagementType
  );
  const initialPortfolio = managementItems.find(
    managementItems => initialManagementType === managementItems.managementType
  );

  if (
    managementItem === undefined ||
    initialManagementItemInfo === undefined ||
    initialPortfolio === undefined
  )
    return <></>;

  const dispInitialPortfolios = initialManagementItemInfo.dispType.map(
    dispType => {
      return initialPortfolio.itemsByPortfolioAnalysis.find(
        item => item.assetType === dispType
      );
    }
  ) as PortfolioAnalysisItem[] | undefined;

  const investmentAssetDetailList = [
    {
      title: '・株式のみでグローバル投資',
      text:
        '投資アセットクラスを株式に限定した上で、国内、新興国、先進国の３地域に分散投資します。単一地域のみに株式で投資した際に比較しリスクの極小化を目指します。',
    },
    {
      title: '・景気拡大予想の分散投資',
      text:
        '株式の期待リターンを標準よりも高め（均衡リターンよりもx％高く）に設定した上で、グローバル分散投資を行います。',
    },
    {
      title: '・景気縮小予想の分散投資',
      text:
        '株式の期待リターンを標準よりも低めに設定した上で、債券、REITが多めのグローバル分散投資を行います。',
    },
    {
      title: '・先進国強気予想の分散投資',
      text:
        '先進国株式の期待リターンを標準よりも高めに設定した上で、グローバル分散投資を行います。',
    },
    {
      title: '・新興国強気予想の分散投資',
      text:
        '新興国株式の期待リターンを標準よりも高めに設定した上で、グローバル分散投資を行います。',
    },
    {
      title: '・国内強気予想の分散投資',
      text:
        '国内株式の期待リターンを標準よりも高めに設定した上で、グローバル分散投資を行います。',
    },
    {
      title: '・アセプラ 8 Types 分散投資',
      text:
        '過去のデータの平均値を活用して、ベーシックな期待リターンにしたアセット設定したものを、詳細に分けて8分類で表示します。',
    },
    {
      title: '・アセプラおすすめ分散投資',
      text:
        '過去のデータの平均値を活用して、ベーシックな期待リターンにしたアセット設定になります。５分類で表示します。',
    },
  ];

  return (
    <div className="p-4 bg-white">
      <div className="flex flex-row">
        <div className="items-start">
          <AvatarIcon icon="average" />
        </div>
        <div className="flex flex-col text-bluegray-700 pl-4">
          <div className="font-bold text-base">
            あなたが興味ある投資先を
            <br />
            １つ選択してください。
          </div>
        </div>
      </div>
      <div className="mt-4 text-base font-bold">アセプラおすすめ</div>

      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.アセプラ5,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.アセプラ5),
          showModal,
        }}
      />
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.アセプラ8,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.アセプラ8),
          showModal,
        }}
      />

      <div className="mt-8 text-base font-bold">株式へ集中投資したい</div>
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.株式のみ,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.株式のみ),
          showModal,
        }}
      />

      <div className="mt-8 text-base font-bold">
        世界の景気動向から投資したい
      </div>
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.景気拡大,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.景気拡大),
          showModal,
        }}
      />
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.景気縮小,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.景気縮小),
          showModal,
        }}
      />

      <div className="mt-8 text-base font-bold">投資地域でカスタマイズ</div>
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.国内強気,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.国内強気),
          showModal,
        }}
      />
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.先進国強気,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.先進国強気),
          showModal,
        }}
      />
      <InvestmentItem
        {...{
          managementType: ManagementTypeItem.新興国強気,
          initialManagementType,
          goAssetConfirm: () => goAssetConfirm(ManagementTypeItem.新興国強気),
          showModal,
        }}
      />

      <div className="mt-6 text-xs text-bluegray-500">
        <div>【投資アセット詳細】</div>
        {investmentAssetDetailList.map((item, i) => {
          return (
            <React.Fragment key={i}>
              <div className="mt-3">
                <div>{item.title}</div>
                <div>{item.text}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <SettingManagementTypeModal
        {...{
          isShow: isShowModal,
          hide: hideModal,
          initialManagementType,
          portfolios: dispInitialPortfolios,
        }}
      />
    </div>
  );
};

const PortfolioAnalysisChangeManagementSelect = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  useCustomHeaderTitle('運用タイプ変更画面');

  const { birthday } = useBirthday();

  const { data: portfolioAnalysis, isFetching } = useQuery(
    'portfolioAnalysisChangeManagementSelectPortfolioAnalysis',
    () => getPortfolioAnalysisAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (
    portfolioAnalysis === null ||
    portfolioAnalysis === undefined ||
    isFetching
  )
    return <PortfolioAnalysisLoading />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <PortfolioAnalysisChangeManagementSelectInternal
        {...{ targetId: id, portfolioAnalysis }}
      />
    </PremiumCheck>
  );
};

const getPortfolioAnalysisAPI = async (id: string) => {
  const ret_ = await getPortfolioAnalysis(id);
  const ret = {
    itemsByPortfolioAnalysis: ret_.ItemsByPortfolioAnalysis.map(item => {
      return {
        assetType: item.AssetType as PortfolioAssetType,
        currentPer: item.CurrentPer,
        targetPer: item.TargetPer,
      };
    }),
    registeredAccountName: ret_.RegisteredAccountName,
    targetReturnPer: ret_.TargetReturnPer,
    targetReturnLimitPer: ret_.TargetReturnLimitPer,
    updateDateOfAsset: ret_.UpdateDateOfAsset,
    managementType: ret_.ManagementType as ManagementType,
    managementItems: ret_.ManagementItems.map(managementItem => {
      return {
        managementType: managementItem.ManagementType as ManagementType,
        itemsByPortfolioAnalysis: managementItem.ItemsByPortfolioAnalysis.map(
          item => {
            return {
              assetType: item.AssetType as PortfolioAssetType,
              currentPer: item.CurrentPer,
              targetPer: item.TargetPer,
            };
          }
        ),
      };
    }),
  };

  return ret as PortfolioAnalysis;
};

export default PortfolioAnalysisChangeManagementSelect;
